/**
 * Main Application Shell
 * Socket is managed by SocketKeeper in App.jsx — do NOT connect/disconnect here.
 */
import React, { useEffect, useState, useCallback } from 'react';
import useStore from '../store/useStore';
import useAudio from '../hooks/useAudio';
import ChannelList from './ChannelList';
import ChannelView from './ChannelView';
import Header from './Header';
import SOSButton from './SOSButton';
import axios from 'axios';

class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(error, info) { console.error('[ErrorBoundary]', error, info); }
  render() {
    if (this.state.hasError) {
      return (
        <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
          <div className="text-s-red text-lg font-bold mb-2 tracking-apple-tight">Error al renderizar</div>
          <pre className="text-xs text-n-500 opacity-80 max-w-md whitespace-pre-wrap mb-4">
            {this.state.error?.toString()}
          </pre>
          <button
            onClick={() => this.setState({ hasError: false, error: null })}
            className="px-4 py-2 rounded-md text-sm font-semibold text-white shadow-apple-md"
            style={{ background: 'linear-gradient(135deg,#007AFF,#AF52DE)' }}>
            Reintentar
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function MainApp() {
  const userId = useStore(s => s.user?.id);
  const setChannels = useStore(s => s.setChannels);
  const updateChannelCount = useStore(s => s.updateChannelCount);
  const socketStatus = useStore(s => s.socketStatus);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useAudio();

  const loadCounts = useCallback(() => {
    axios.get('/api/channels/online-counts')
      .then(res => {
        Object.entries(res.data).forEach(([id, count]) => updateChannelCount(id, count));
      })
      .catch(() => {});
  }, [updateChannelCount]);

  useEffect(() => {
    if (!userId) return;
    axios.get('/api/channels').then(res => setChannels(res.data)).catch(() => {});
    loadCounts();
    const t = setInterval(loadCounts, 30000);
    return () => clearInterval(t);
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="flex flex-col h-screen overflow-hidden">
      <Header onMenuToggle={() => setSidebarOpen(o => !o)} />
      {(socketStatus === 'disconnected' || socketStatus === 'reconnecting') && (
        <div
          role="alert"
          className="flex items-center justify-center gap-2 px-4 py-2 text-sm font-bold text-white flex-shrink-0"
          style={{ background: socketStatus === 'disconnected' ? '#FF3B30' : '#FF9500' }}
        >
          {socketStatus === 'disconnected'
            ? 'Sin conexión — no estás recibiendo transmisiones'
            : 'Reconectando…'}
        </div>
      )}
      <div className="flex flex-1 overflow-hidden">
        {sidebarOpen && (
          <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-20 lg:hidden" onClick={() => setSidebarOpen(false)} />
        )}
        <aside
          className={`
            fixed lg:static top-[60px] lg:top-auto left-0 bottom-0 lg:bottom-auto
            w-72 flex-shrink-0 flex flex-col z-30 lg:z-auto
            transition-transform duration-300 ease-in-out
            ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          `}
          style={{
            background: 'rgba(255,255,255,0.78)',
            WebkitBackdropFilter: 'saturate(180%) blur(30px)',
            backdropFilter: 'saturate(180%) blur(30px)',
            borderRight: '1px solid rgba(0,0,0,0.06)',
          }}
        >
          <ChannelList onChannelSelect={() => setSidebarOpen(false)} />
        </aside>
        <main className="flex-1 flex flex-col overflow-hidden min-w-0">
          <ErrorBoundary>
            <ChannelView />
          </ErrorBoundary>
        </main>
      </div>
      <SOSButton />
    </div>
  );
}
